import RemoteDataTable from '@components/remote-data-table/RemoteDataTable';
import useSession from '@hooks/useSession';
import { useNavigate } from 'react-router-dom';
import { useFileIngestionLogsData } from '@pages/file-ingestion/useFileIngestionLogsData';
import FileIngestionUploadSection from '@components/file-ingestion-upload/FileIngestionUploadSection';
import PageLayout from '@components/layout/PageLayout';
import RejectsTable from '@components/rejects-table/RejectsTable';
import styles from './FileIngestionPage.module.css';


const RejectsRow = ({ rejects }: { rejects: any[] }) => (
  <RejectsTable rejects={rejects} />
);

const renderExpandableRow = (record) => {
  const hasRejects = record.rejects && record.rejects.length > 0;

  return (
    <div>
      {hasRejects && <RejectsRow rejects={record.rejects} />}
    </div>
  );
};

const FileIngestionPage = () => {
  const { isAdmin, isSuperAdmin, isUser } = useSession();
  const navigate = useNavigate();

  const handleStatusClick = (record) => {
    navigate(`/file-upload-status/${record.id}`);
  };

  const renderPipelineStatus = (pipelineStatus, record) => {
    if (!pipelineStatus) return '-';

    const normalized = pipelineStatus.toLowerCase();

    const statusMap = {
      completed: { icon: '✅', label: 'Completed' },
      warning: { icon: '✅', label: 'Completed (with ⚠️ warnings)' },
      failed: { icon: '❌', label: 'Failed' },
      cancelled: { icon: '❌', label: 'Cancelled' },
      running: { icon: '🔄', label: 'Running' },
    };

    const status = statusMap[normalized] || { icon: '', label: pipelineStatus };

    const statusClass = `status-${normalized}`;

    return (
      <button
        type="button"
        onClick={() => handleStatusClick(record)}
        className={`${styles.statusButton} navlink ${statusClass}`}
      >
        {status.icon} {status.label}
      </button>
    );
  };
  const renderFileStatus = (fileStatus) => {
    if (!fileStatus) return '-';

    const normalized = fileStatus.toLowerCase();

    const statusMap = {
      success: { icon: '✅', label: 'Success' },
      failed: { icon: '❌', label: 'Failed' },
    };

    const status = statusMap[normalized] || { icon: '', label: fileStatus };
    const statusClass = `status-${normalized}`;

    return (
      <span className={`${styles.statusText} ${statusClass}`}>
        {status.icon} {status.label}
      </span>
    );
  };

  const renderRecordCount = (recordCount, record) => {
    const rejectCount = record.rejectRecordCount || 0;

    if (rejectCount === 0) {
      return recordCount || 0;
    }

    return (
      <div className={styles.recordCountContainer}>
        <span className={styles.successCount}>{recordCount || 0}</span>
        <span className={styles.rejectCount}>
          ({rejectCount} rejected)
        </span>
      </div>
    );
  };

  return (
    <PageLayout title="File Upload Logs">
      <div className={styles.content}>
        {(isAdmin || isSuperAdmin || isUser) && <FileIngestionUploadSection />}

        <RemoteDataTable
          useData={useFileIngestionLogsData}
          rowKey="id"
          columnStructures={{
            id: { title: 'Id' },
            fileName: {
              title: 'File Name',
            },
            pipelineStatus: {
              title: 'Pipeline Status',
              render: renderPipelineStatus,
            },
            fileStatus: {
              title: 'File Status',
              render: renderFileStatus,
            },
            recordCount: {
              title: 'Meter Count',
              render: renderRecordCount,
            },
            ingestionTimestampFormatted: {
              title: 'Upload Time',
            },
            monthYear: {
              title: 'Month-Year',
              render: (monthYear) => monthYear || '-',
            },
          }}
          columnsToIgnore={[
            'id',
            'errorMessage',
            'ingestionTimestamp',
            'rejects',
            'rejectRecordCount',
            '__typename'
          ]}
          expandableRowRender={renderExpandableRow}
          expandableCondition={(record) => {
            const status = record.pipelineStatus
              ?.replaceAll("'", "")
              .replaceAll('"', "")
              .toLowerCase();

            const error = record.errorMessage
              ?.replaceAll("'", "")
              .replaceAll('"', "")
              .replaceAll("\n", "")
              .trim();

            const hasErrors = (status === "failed" || status === "error") && error && error.length > 0;
            const hasRejects = record.rejects && record.rejects.length > 0;

            return hasErrors || hasRejects;
          }}
        />
      </div>
    </PageLayout>
  );
};

export default FileIngestionPage;